import { useMonitorShipSelections } from '@ais-ui/api';
import { Ship } from '@ais-ui/api-spec/portserver';
import { useShipSelection } from './contexts/ShipSelectionContext';
import { useEffect, useMemo, useRef } from 'react';
import { AutoCompleteShipInfo } from '@ais-ui/websockets';
import { useSettings } from '../Settings/SettingsProvider';
import { useToast } from '@ais-ui/ui-core';
import { useLocalization } from '@ais-ui/locale-management';
import { useRemoteZoomPulse } from './contexts/RemoteZoomPulseContext';
import {
  addRemoteZoomBreadcrumb,
  reportRemoteSelectionFailure,
  useReportSlowRemotePoll,
} from './remoteZoomTelemetry';

const remoteZoomTarget = (
  overrides: Pick<AutoCompleteShipInfo, 'name' | 'mmsi' | 'imo'>,
): AutoCompleteShipInfo => ({
  callSign: '',
  type: 'TT_SHIP',
  forceView: true,
  source: 'remote',
  ...overrides,
});

// Prefer to zoom using MMSI, then IMO, then name, do not use all see JIRA issue here with the ship Albert
//  CLASSIC-9281 which suddenly changed their name and imo in the AIS message
const toRemoteZoomTarget = (ship: Ship): AutoCompleteShipInfo | null => {
  if (ship.mmsi) {
    return remoteZoomTarget({ name: '', mmsi: String(ship.mmsi), imo: -1 });
  }
  if (ship.imo) {
    return remoteZoomTarget({ name: '', mmsi: null, imo: String(ship.imo) });
  }
  const mmsiName = ship.name !== ship.mmsi ? ship.name : '';
  if (mmsiName) {
    return remoteZoomTarget({ name: mmsiName, mmsi: null, imo: -1 });
  }
  return null;
};

export const useZoomToExternalShip = () => {
  const { settings } = useSettings();
  const { messages } = useLocalization();

  const isRemoteControlEnabled = useMemo(() => {
    return settings['ribbonPlugin.settings']?.remotecontrol_enabled ?? false;
  }, [settings]);

  const { toast } = useToast();

  const { data, isError, error, isFetching } = useMonitorShipSelections(
    isRemoteControlEnabled,
  );
  useReportSlowRemotePoll(isFetching);

  const { setZoomToShip } = useShipSelection();
  const { triggerPulse } = useRemoteZoomPulse();

  // Separate from the data effect: a failing poll may never have produced data.
  // Toast/report once when entering the error state, not for every failed poll.
  const hasReportedPollError = useRef(false);
  useEffect(() => {
    if (!isError) {
      hasReportedPollError.current = false;
      return;
    }
    if (hasReportedPollError.current) {
      return;
    }

    hasReportedPollError.current = true;
    toast({
      title: messages.genericToastTitleError,
      description: 'Error fetching selected ship data',
      variant: 'destructive',
    });
    reportRemoteSelectionFailure('poll-error', error);
  }, [isError, error, messages.genericToastTitleError, toast]);

  useEffect(() => {
    // React Query keeps the last data when a refetch fails; don't act on it
    if (isError) {
      return;
    }

    const selectedShips = data?.records?.[0]?.ship;
    if (!selectedShips) {
      return;
    }

    if (selectedShips.length === 0) {
      reportRemoteSelectionFailure('empty-ship-list');
      toast({
        title: messages.genericToastTitleError,
        description: "Couldn't find vessel requested from remote access",
        variant: 'destructive',
      });
      return;
    }

    const ship = selectedShips[0];
    addRemoteZoomBreadcrumb('selection received', {
      mmsi: ship.mmsi,
      imo: ship.imo,
      name: ship.name,
    });

    const target = toRemoteZoomTarget(ship);
    if (!target) {
      reportRemoteSelectionFailure('no-ship-identifier');
      return;
    }

    setZoomToShip(target);
    triggerPulse();
  }, [
    data,
    isError,
    messages.genericToastTitleError,
    setZoomToShip,
    triggerPulse,
    toast,
  ]);
};
